import { Callout, HelpLink, Related, ScreenLink } from "./parts";

export function Installer() {
  return (
    <>
      <p>
        The <ScreenLink href="/installer">Installer</ScreenLink> deploys and upgrades the Nexus proxy in a customer's AWS
        account. An AI agent (Claude on Bedrock, in AllCode's account) looks at the account, proposes a configuration and
        turns it into a CloudFormation change set or a Terraform plan. <strong>A person approves every write.</strong> The
        installer then applies it, checks the proxy works, and registers the installation. You need the Installer role.
      </p>

      <h2 id="linking-an-account">Linking an AWS account</h2>
      <p>Every install needs a verified link to the customer's account. The link is a small CloudFormation stack the customer launches; it holds the roles the installer works through.</p>
      <ol>
        <li>
          Open the customer under Installer → AWS accounts and choose <strong>Link account</strong>. Enter the 12-digit
          account id and the capabilities: <strong>discover</strong> (always), <strong>deploy</strong> (CloudFormation),{" "}
          <strong>terraform</strong>, and <strong>cur</strong> (the Cost and Usage Report for AWS costs).
        </li>
        <li>
          <strong>Copy the external id and the quick-create link now.</strong> They are shown once. If you lose them, delete
          the link and link again.
        </li>
        <li>
          The customer opens the quick-create link in their AWS console and launches the stack as filled in: name{" "}
          <code>nexus-bootstrap-&lt;link id&gt;</code>, region <code>us-east-1</code>.
        </li>
        <li>
          <strong>Verify</strong>. The link shows when it was verified and the stack's version.
        </li>
      </ol>
      <p>
        Verify fails most often because the stack is missing, in another region, or was launched with another link's
        external id. Capabilities cannot be changed later: link again with the new set.
      </p>

      <h2 id="access-modes">Access modes</h2>
      <table>
        <thead>
          <tr>
            <th>Mode</th>
            <th>You supply</th>
            <th>Use when</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Bootstrap role</td>
            <td>Nothing: the installer uses the link's role</td>
            <td>The normal, long-term path: upgrades and repeat installs</td>
          </tr>
          <tr>
            <td>Temporary keys</td>
            <td>
              Short-lived AWS keys for the account (<code>ASIA…</code>; long-term <code>AKIA…</code> keys are refused)
            </td>
            <td>A one-off install where the customer hands over a short session</td>
          </tr>
          <tr>
            <td>IAM Identity Center</td>
            <td>A sign-in in your browser, then an account and permission set</td>
            <td>The customer gives you an Identity Center permission set</td>
          </tr>
        </tbody>
      </table>
      <p>
        Keys and Identity Center sign-ins are held in memory for at most an hour, never stored or shown to the agent. If the
        admin service restarts, the session asks for them again. With keys or Identity Center, the approval step is the only
        guard on writes; with the bootstrap role, the stack's permission limits apply as well.
      </p>

      <h2 id="sessions">Sessions</h2>
      <p>
        To start one (<strong>Start session</strong> on the Installer screen), pick the customer, AWS account, regions, the goal (install, or upgrade an
        installation) and the access mode. One live session per customer and account.
      </p>
      <p>
        A session moves through: created → awaiting credentials → discovering → planning → awaiting approval → applying →
        verifying → done. It can also be paused, failed or cancelled. In the console you can:
      </p>
      <ul>
        <li>send the agent a message, such as “use the existing VPC vpc-0abc” or “internal load balancer only”;</li>
        <li>approve or reject its proposed changes;</li>
        <li>extend the budget of a paused session;</li>
        <li>cancel. What was already applied stays in place.</li>
      </ul>
      <p>
        Limits per session: $100 of model spend, 60 turns and 2 hours by default. Reaching one pauses the session until
        someone extends or cancels it. All sessions together also have a daily budget ($1,000 by default); when it is used
        up, new sessions wait until midnight UTC.
      </p>

      <h2 id="approving-changes">Approving changes</h2>
      <p>An install proposes two changes, in order: registering the installation (and storing its secret in the customer's Secrets Manager), then the proxy stack itself. Each shows:</p>
      <ul>
        <li>a summary: resources added, changed, replaced or removed, and parameter changes;</li>
        <li>
          <strong>risk flags</strong>, such as a public load balancer, open ingress, IAM changes, deletions or replacements,
          prompt logging on, or non-default parameters. Flags never block; they mark what to read twice;
        </li>
        <li>an expiry (24 hours) and the change's <strong>digest</strong>.</li>
      </ul>
      <p>
        <strong>Approve</strong> binds your approval to that exact digest and asks you to confirm. Just before writing, the
        installer checks the digest again; if anything changed, nothing is written and you review again.{" "}
        <strong>Reject</strong> with a reason sends the agent back to planning.
      </p>
      <Callout>
        Look twice at a replacement or deletion flag on an upgrade before approving: it can mean downtime or lost
        configuration.
      </Callout>
      <p>
        When the stack is applied and the proxy has sent a heartbeat, the session finishes and the installation appears on
        the customer's AWS accounts tab. See <HelpLink to="installations" />.
      </p>

      <Related to={["installations", "aws-costs", "audit-log"]} />
    </>
  );
}
